import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { UserType } from "../../../constants";
import { TabsList } from "../../../models";
import { getPipelineAggregate } from "../../../service/query";
import { Status } from "../../../service/Shared";
import { useAuth } from "../../../useAuth";
import { getNewDealRoute, getPipelineRoadmapRoute, getPipelineTrackerRoute } from "../../../utils/routes";
import { ChartData } from "../../../widget";

interface UsePortfolioReturnData {
  tabsList: TabsList;
  isLoading: boolean;
  currentUserType: UserType;
  dealTypeChartData: ChartData;
  dealStageChartData: ChartData;
  dealsClosedChartData: ChartData;
  dealTypeOrStage: string;
  predictedReturns: number | undefined;
  plannedInvestment: number | undefined;
  onNewDealClick: () => void;
  setDealTypeOrStage: Dispatch<SetStateAction<string>>;
}
export const usePortfolio = (): UsePortfolioReturnData => {
  const navigate = useNavigate();

  const { currentUserType, currentOrganisationUuid } = useAuth();
  const [dealTypeChartData, setDealTypeChartData] = useState<ChartData>({});
  const [dealStageChartData, setDealStageChartData] = useState<ChartData>({});
  const [dealsClosedChartData, setDealsClosedChartData] = useState<ChartData>({});

  const [predictedReturns, setPredictedReturns] = useState<number>();
  const [plannedInvestment, setPlannedInvestment] = useState<number>();
  const [dealTypeOrStage, setDealTypeOrStage] = useState<string>("By type");

  const [isLoading, setIsLoading] = useState(true);

  const onNewDealClick = (): void => {
    navigate(getNewDealRoute());
  };

  const tabsList = [
    {
      label: "Tracker",
      route: getPipelineTrackerRoute(currentUserType),
    },
    {
      label: "Roadmap",
      route: getPipelineRoadmapRoute(currentUserType),
    },
  ];

  const fetchData = useCallback(
    async (organisationUuid: string) => {
      const [
        dealTypeAggregateRes,
        dealStageAggregateRes,
        targetReturnsAggregateRes,
        investmentValueAggregateRes,
        dealsClosedAggregateRes,
      ] = await Promise.all([
        getPipelineAggregate({
          organisationUuid,
          pipelineUuid: null,
          filters: [],
          groupBy: "dealtype",
          aggregation: "dealcount",
        }),
        await getPipelineAggregate({
          organisationUuid,
          pipelineUuid: null,
          filters: [],
          groupBy: "stage",
          aggregation: "dealcount",
        }),
        await getPipelineAggregate({
          organisationUuid,
          pipelineUuid: null,
          filters: [],
          groupBy: "pipelineUuid",
          aggregation: "targetreturns",
        }),
        await getPipelineAggregate({
          organisationUuid,
          pipelineUuid: null,
          filters: [],
          groupBy: "pipelineUuid",
          aggregation: "investmentvalue",
        }),
        await getPipelineAggregate({
          organisationUuid,
          pipelineUuid: null,
          filters: [],
          groupBy: "status",
          aggregation: "dealsclosed",
        }),
      ]);

      if (dealTypeAggregateRes.status === Status.Success && dealTypeAggregateRes.data) {
        const dealTypeData = dealTypeAggregateRes.data.results.reduce<ChartData>((previous, current) => {
          // eslint-disable-next-line no-param-reassign
          previous[current.key] = Number(current.value);
          return previous;
        }, {});
        setDealTypeChartData(dealTypeData);
      }

      if (dealStageAggregateRes.status === Status.Success && dealStageAggregateRes.data) {
        const dealStageData = dealStageAggregateRes.data.results.reduce<ChartData>((previous, current) => {
          // eslint-disable-next-line no-param-reassign
          previous[current.key] = Number(current.value);
          return previous;
        }, {});
        setDealStageChartData(dealStageData);
      }

      if (dealsClosedAggregateRes.status === Status.Success && dealsClosedAggregateRes.data) {
        const dealsClosedData = dealsClosedAggregateRes.data.results.reduce<ChartData>((previous, current) => {
          // eslint-disable-next-line no-param-reassign
          previous[current.key] = Number(current.value);
          return previous;
        }, {});
        setDealsClosedChartData(dealsClosedData);
      }

      if (targetReturnsAggregateRes.status === Status.Success && targetReturnsAggregateRes.data) {
        const targetReturnsData = targetReturnsAggregateRes.data.results.reduce<number>(
          (previous, current) => previous + Number(current.value),
          0
        );
        setPredictedReturns(targetReturnsData);
      }

      if (investmentValueAggregateRes.status === Status.Success && investmentValueAggregateRes.data) {
        const investmentValueData = investmentValueAggregateRes.data.results.reduce<number>(
          (previous, current) => previous + Number(current.value),
          0
        );

        setPlannedInvestment(investmentValueData);
      }
    },
    [currentOrganisationUuid]
  );

  useEffect(() => {
    if (currentOrganisationUuid) {
      fetchData(currentOrganisationUuid).finally(() => {
        setIsLoading(false);
      });
    }
  }, [currentOrganisationUuid]);

  return {
    tabsList,
    isLoading,
    currentUserType,
    dealTypeChartData,
    dealStageChartData,
    dealsClosedChartData,
    dealTypeOrStage,
    predictedReturns,
    plannedInvestment,
    onNewDealClick,
    setDealTypeOrStage,
  };
};
